import * as React from "react";

import { useEphemeralWorkspaceState } from "@/shared/components/visualization/WorkspaceCtx";
import type { FileRecord } from "@/shared/domain/files";
import {
  CanonicalDataType,
  MessagePathPartType,
  type MessagePathAttr,
  type TopicRecord,
} from "@/shared/domain/topics";

import type { TopicDataPathBinding } from "./positionTopicData";
import { PositionPreset, presets } from "./presets";

/**
 * Presets bound to the position topics carried by the workspace's open files,
 * one per matching topic, so a user can pick a working position without naming
 * a path for each axis.
 */
export function useWorkspacePositionPresets() {
  const state = useEphemeralWorkspaceState();
  return React.useMemo(
    // TODO(dave): we can pull this out into a hook, and use it to handle topic drag-and-drop.
    // Any dropped topic that matches a preset would be loaded.
    () => workspacePositionPresets(state.files, state.topics),
    [state.files, state.topics],
  );
}

/**
 * ROS message types that carry a position, and the field holding it. A ROS
 * frame is forward/left/up (REP-103), so these bind the Forward/Left/Up preset.
 */
const ROS_POSITION_FIELD: Record<string, string> = {
  "nav_msgs/Odometry": "pose.pose.position",
  "geometry_msgs/PoseStamped": "pose.position",
  "geometry_msgs/PoseWithCovarianceStamped": "pose.pose.position",
  "geometry_msgs/PointStamped": "point",
  "geometry_msgs/TransformStamped": "transform.translation",
};

/** The position field of a ROS message type, spelled the ROS 1 or ROS 2 way. */
function rosPositionField(schemaName: string | null): string | undefined {
  // ROS 2 names a type `package/msg/Type`, ROS 1 `package/Type`.
  return schemaName
    ? ROS_POSITION_FIELD[schemaName.replace("/msg/", "/")]
    : undefined;
}

/**
 * The presets the workspace's topics offer: PX4's position topics by name, and
 * ROS topics by message type.
 */
export function workspacePositionPresets(
  files: Pick<FileRecord, "file_id" | "name">[],
  topics: TopicRecord[],
): PositionPreset[] {
  const prefilled: PositionPreset[] = [];
  const idToFile = Object.fromEntries(files.map((f) => [f.file_id, f]));
  const titled = (name: string, file: Pick<FileRecord, "name">) =>
    files.length > 1 ? `${name} (${file.name})` : name;

  for (const topic of topics) {
    const file = idToFile[topic.association.association_id];
    if (!file) {
      continue;
    }
    if (topic.topic_name === "vehicle_global_position") {
      prefilled.push(
        specifyPreset({
          preset: presets.lat_lon_alt,
          title: titled("vehicle_global_position (PX4)", file),
          fileId: file.file_id,
          topic,
          rightAttribute: "lat",
          forwardAttribute: "lon",
          upAttribute: "alt_ellipsoid",
        }),
      );
    }
    if (topic.topic_name === "vehicle_local_position") {
      prefilled.push(
        specifyPreset({
          preset: presets.north_east_down,
          title: titled("vehicle_local_position (PX4)", file),
          fileId: file.file_id,
          topic,
          forwardAttribute: "x",
          rightAttribute: "y",
          upAttribute: "z",
        }),
      );
    }
    const rosField = rosPositionField(topic.schema_name);
    if (rosField !== undefined) {
      prefilled.push(
        specifyPreset({
          preset: presets.forward_left_up,
          title: titled(`${topic.topic_name} (ROS)`, file),
          fileId: file.file_id,
          topic,
          forwardAttribute: `${rosField}.x`,
          rightAttribute: `${rosField}.y`,
          upAttribute: `${rosField}.z`,
        }),
      );
    }
  }
  return prefilled;
}

/**
 * A copy of a coordinate-convention preset with each axis bound to a field of
 * one topic.
 */
function specifyPreset({
  preset,
  title,
  fileId,
  topic,
  forwardAttribute,
  rightAttribute,
  upAttribute,
}: {
  preset: PositionPreset;
  title: string;
  fileId: string;
  topic: TopicRecord;
  forwardAttribute: string;
  rightAttribute: string;
  upAttribute: string;
}): PositionPreset {
  return {
    ...preset,
    name: {
      // A hint at the file formats that use the convention is redundant once
      // the preset names a topic from a file already open.
      formatHint: "",
      subtitle: `${preset.name.title} (${preset.name.subtitle})`,
      title,
    },
    bindings: {
      forward: bindingForAttribute(topic, fileId, forwardAttribute),
      right: bindingForAttribute(topic, fileId, rightAttribute),
      up: bindingForAttribute(topic, fileId, upAttribute),
    },
  };
}

/**
 * Binds one axis to a field of a topic, named by its dotted path, or undefined
 * when the topic declares no field there. A preset is picked by topic name or
 * message type alone, so a file can carry the topic without the field an axis
 * wants.
 *
 * Each level of a nested field is its own attribute part, read from the
 * record's `path_in_schema`, which keeps a name containing a dot in one piece.
 */
function bindingForAttribute(
  topic: TopicRecord,
  fileId: string,
  attribute: string,
): TopicDataPathBinding | undefined {
  const messagePath = topic.message_paths.find(
    (candidate) => candidate.message_path === attribute,
  );
  if (messagePath === undefined) {
    return undefined;
  }

  const levels = messagePath.path_in_schema;
  const parts: MessagePathAttr[] = levels.map((level, index) => {
    const prefix = levels.slice(0, index + 1).join(".");
    const record =
      index === levels.length - 1
        ? messagePath
        : topic.message_paths.find(
            (candidate) => candidate.message_path === prefix,
          );
    return {
      type: MessagePathPartType.Attr,
      attribute: level,
      dataType: record?.canonical_data_type ?? CanonicalDataType.Object,
    };
  });

  return {
    fileId,
    topicName: topic.topic_name,
    messagePath: parts,
  };
}
